import { EM_DASH, ISRAEL_TIME_NOTE, formatIsraelDateTime } from "@/lib/format";
import type {
  LabAttentionRow,
  LabEventRow,
  LabRun,
  LabRunModel,
} from "@/lib/labRunModel";
import {
  EVENT_KIND_LABELS,
  EVENT_STATUS_TONES,
  type TraceEventStatus,
} from "@/lib/previewDecisions";
import { Badge } from "@/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { EmptyState } from "@/ui/states";

/**
 * Run timeline: consecutive agent and track runs with their turn range and
 * wall-clock duration, the runtime transitions, and the events a clinician
 * must not skip (attention, failed, unavailable, unreadable). Durations are
 * the model's; a run without two readable timestamps says "not recorded".
 */

type Props = {
  projectId: string;
  model: LabRunModel;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

const ROW =
  "flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-card px-3 py-2 text-start text-sm hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring";

function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return "not recorded";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return `${minutes} min ${rest} s`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

function turnRange(run: LabRun): string {
  return run.fromTurn === run.toTurn
    ? `turn ${run.fromTurn}`
    : `turns ${run.fromTurn}-${run.toTurn}`;
}

function None() {
  return <p className="text-sm text-muted-foreground">None recorded</p>;
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

function RunRows({
  runs,
  onSelectTurn,
  noun,
}: {
  runs: LabRun[];
  onSelectTurn: (t: number) => void;
  noun: string;
}) {
  if (runs.length === 0) return <None />;
  return (
    <ul className="space-y-1">
      {runs.map((run, index) => (
        <li key={`${run.fromTurn}-${index}`}>
          <button
            type="button"
            className={ROW}
            aria-label={`${noun} ${run.label ?? "not recorded"}, ${turnRange(run)}, duration ${formatDuration(run.durationMs)}`}
            onClick={() => onSelectTurn(run.fromTurn)}
          >
            <span dir="auto" className="font-medium text-foreground">
              {run.label ?? "not recorded"}
            </span>
            <span className="text-muted-foreground">{turnRange(run)}</span>
            <span className="text-muted-foreground">
              {formatDuration(run.durationMs)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function kindLabel(kind: string): string {
  return (EVENT_KIND_LABELS as Record<string, string>)[kind] ?? kind;
}

function statusTone(status: string) {
  return (
    (
      EVENT_STATUS_TONES as Record<
        string,
        "neutral" | "outline" | "warning" | "destructive"
      >
    )[status as TraceEventStatus] ?? "outline"
  );
}

function EventRows({
  rows,
  onSelectTurn,
}: {
  rows: readonly (LabEventRow | LabAttentionRow)[];
  onSelectTurn: (t: number) => void;
}) {
  if (rows.length === 0) return <None />;
  return (
    <ul className="space-y-1">
      {rows.map((row) => {
        const body = (
          <>
            <Badge tone={statusTone(row.status)}>{row.statusLabel}</Badge>
            <span className="text-muted-foreground">{kindLabel(row.kind)}</span>
            <span dir="auto" className="min-w-0 break-words text-foreground">
              {row.label}
            </span>
            <span className="text-muted-foreground">
              {row.turnNumber !== null ? `turn ${row.turnNumber}` : "no turn"}
            </span>
            <span
              dir="ltr"
              title={ISRAEL_TIME_NOTE}
              className="text-xs text-muted-foreground"
            >
              #{row.sequence}
              {row.occurredAt !== null
                ? ` · ${formatIsraelDateTime(row.occurredAt)}`
                : ` · ${EM_DASH}`}
            </span>
          </>
        );
        return (
          <li key={row.eventRef}>
            {row.turnNumber !== null ? (
              <button
                type="button"
                className={ROW}
                onClick={() => onSelectTurn(row.turnNumber as number)}
              >
                {body}
              </button>
            ) : (
              <div className={ROW.replace("hover:bg-muted ", "")}>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function LabTimelineTab({ model, onSelectTurn }: Props) {
  const t = model.timeline;
  return (
    <section aria-labelledby="lab-timeline-heading" className="space-y-4">
      <h2
        id="lab-timeline-heading"
        className="text-base font-semibold text-foreground"
      >
        Timeline
      </h2>

      {model.lab.turns.length === 0 ? (
        <EmptyState
          title="No turns recorded yet"
          description="Agent and track runs appear once the run has captured a turn."
        />
      ) : null}

      <Section title="Agent runs">
        <RunRows runs={t.agentRuns} onSelectTurn={onSelectTurn} noun="Agent" />
      </Section>
      <Section title="Track runs">
        <RunRows runs={t.trackRuns} onSelectTurn={onSelectTurn} noun="Track" />
      </Section>

      <Section title="Transitions">
        {t.markers.length === 0 ? (
          <None />
        ) : (
          <ul className="space-y-1">
            {t.markers.map((marker) => (
              <li key={marker.eventRef}>
                <button
                  type="button"
                  className={ROW}
                  onClick={() => onSelectTurn(marker.turnNumber)}
                >
                  <span
                    dir="auto"
                    className="min-w-0 break-words text-foreground"
                  >
                    {marker.from} → {marker.to}
                  </span>
                  <Badge tone="accent">{marker.reasonLabel}</Badge>
                  <span className="text-muted-foreground">
                    turn {marker.turnNumber}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Needs attention">
        <EventRows rows={t.attention} onSelectTurn={onSelectTurn} />
      </Section>

      <Section title="Before the first turn">
        <EventRows rows={t.preTurn} onSelectTurn={onSelectTurn} />
      </Section>

      <Section title="Unreadable detail">
        <EventRows rows={t.unreadable} onSelectTurn={onSelectTurn} />
        {t.unreadableRecordCount > 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {t.unreadableRecordCount}{" "}
            {t.unreadableRecordCount === 1 ? "record" : "records"} could not be
            placed by this build
          </p>
        ) : null}
      </Section>
    </section>
  );
}
